import { getCreateReleaseEndpoint, getPublishReleaseEndpoint } from "apis/magnets";
import clsx from "clsx";
import ButtonWithIcon from "components/atoms/buttons/ButtonWithIcon";
import { RefreshIcon } from "components/atoms/Icon";
import { LoaderContext } from "components/context/LoaderContext";
import moment from "moment";
import { useContext, useEffect, useState } from "react";
import { toast } from "react-toastify";
import * as yup from "yup";
import { createFormDataFromJSON } from "utils/HelperFunctions";
import { useBrandContext } from "components/context/BrandContext";
import Button from "../../../../components/atoms/buttons/Button";
import { BRAND_MAGNET, RELEASE, TOAST_CONFIG } from "../../../../constants";
import { getReleaseEndEndpoint, getReleaseUpdateEndpoint, isObjectEmpty } from "../../../../utils/helpers";
import { patch, post, remove } from "../../../../utils/request";
import { ReleaseModal, ReleaseTable } from "./components";

interface IProps {
  isMagnetPublished: boolean;
  releases: Array<Release>;
  magnetMaxSupply: number;
  magnetAvailableSupply: number;
  reloadMagnetDetails: () => void;
  magnetIdFromUrl: string;
  isRefetching: boolean;
}

type CreateUpdateRelease = Omit<
  Release,
  "id" | "sold" | "status" | "total_supply" | "processing_purchases_count" | "whitelisted_file_url"
>;
type ReleaseEventRequest = Pick<ReleaseEvent, "name">;

export type ReleaseStateType = {
  startTime: moment.Moment;
  endTime: moment.Moment | null;
  supplyForRelease: number;
  salePrice: number;
  isWhitelisted: Boolean;
  event: ReleaseEventRequest | {};
};

export default function ReleaseComponent({
  releases,
  magnetMaxSupply,
  magnetAvailableSupply,
  reloadMagnetDetails,
  isRefetching,
  magnetIdFromUrl,
  isMagnetPublished,
}: IProps) {
  const [editReleaseId, setEditReleaseId] = useState<number>();
  const [errorState, setErrorState] = useState<any>({});
  const [openModal, setOpenModal] = useState(false);

  const { brandDetails } = useBrandContext();

  const { setLoading: setGlobalLoader } = useContext(LoaderContext);

  const currentlyEditingRelease = editReleaseId ? releases.find((r) => r.id === editReleaseId) : undefined;

  if (editReleaseId && !currentlyEditingRelease) {
    // TODO: Decide if we need to add Sentry here.
    console.error("FOUND RELEASE ID BUT NO CORRESPONDING RELEASE WAS FOUND");
    return null;
  }
  const maxEndTimeForRelease = (() => {
    return moment();
    // TODO: IMPROVE THIS LOGIC
    const releaseEndTimes = releases.map((r) => r.end_time);

    if (!releaseEndTimes.length) return moment();
    // Add one minute
    // Second Math max makes sure that we don't get a date from the past, in case all the releases have ended.
    return moment(Math.max(Math.max(...releaseEndTimes) + 60 * 1000, new Date().getTime()));
  })();

  const [data, setData] = useState<ReleaseStateType>({
    startTime: maxEndTimeForRelease,
    endTime: null,
    supplyForRelease: magnetAvailableSupply,
    salePrice: releases[releases.length - 1]?.price || 10,
    isWhitelisted: false,
    event: {},
  });

  const [noEndReleaseTime, setNoReleaseTime] = useState(false);
  const [isWhitelistedRelease, setIsWhitelistedRelease] = useState(false);
  const [isReleaseEventCreated, setIsReleaseEventCreated] = useState(false);

  const [isManageMagnetReleaseModalOpen, setIsManageMagnetReleaseModalOpen] = useState(false);

  useEffect(() => {
    if (brandDetails.is_stripe_connected && window.location.href.includes("status=success")) {
      toast.success("Congratulations! Your Stripe account is now connected.", TOAST_CONFIG);
    } else if (!brandDetails.is_stripe_connected && window.location.href.includes("status=failure")) {
      toast.error(
        "We're sorry, but there was an issue connecting your Stripe account. If you continue to experience difficulties, don't hesitate to contact us for assistance. We're here to help you get set up and running smoothly!",
        TOAST_CONFIG,
      );
    }
  }, []);

  // const getOffset = () => {
  //   const offsetInMilliseconds = new Date().getTimezoneOffset() * 60 * 1000;
  //   return offsetInMilliseconds;
  // };

  const getDefaultReleaseData = () => ({
    startTime: maxEndTimeForRelease,
    endTime: null,
    supplyForRelease: magnetAvailableSupply,
    salePrice: releases[releases.length - 1]?.price || 10,
    isWhitelisted: false,
    event: {},
  });

  const handleResetScheduleData = () => {
    setIsWhitelistedRelease(false);
    setIsReleaseEventCreated(false);
    setData(getDefaultReleaseData());
  };

  // const runValidation = async (payload: CreateUpdateRelease) => {
  //   const _checkReleaseAvailability = (userSelectDate: any, availableDate: any) => {
  //     const userSelectStartTime = new Date(userSelectDate);
  //     const releasedStartTime = new Date(utcToLocalDate(availableDate));
  //     if (
  //       userSelectStartTime.getDate() === releasedStartTime.getDate() &&
  //       userSelectStartTime.getFullYear() === releasedStartTime.getFullYear() &&
  //       userSelectStartTime.getMonth() === releasedStartTime.getMonth() &&
  //       userSelectStartTime.getMinutes() === releasedStartTime.getMinutes() &&
  //       userSelectStartTime.getHours() === releasedStartTime.getHours()
  //     ) {
  //       return true;
  //     }
  //     return false;
  //   };

  const runValidation = async (payload: CreateUpdateRelease, isEditRelease: boolean = false) => {
    if (!currentlyEditingRelease) {
      console.error("NO CURRENTLY EDITING RELEASE");
    }
    const maxSupplyValidationNumber = isEditRelease
      ? // When user is editing a release than the max supply is equal to magnetAvailableSupply+existingrelease supply
        Math.max((currentlyEditingRelease?.max_supply as number) + magnetAvailableSupply)
      : magnetAvailableSupply;

    const schema = yup.object().shape({
      start_time: yup
        .number()
        .typeError("Invalid start date")
        .required("Start time is required")
        .test(
          "one-release-same-day",
          "A release already created on this date",
          () => true,
          // const releaseAvailable = releases.find((release) => {
          //   const output = _checkReleaseAvailability(val, release.start_time);
          //   if (output && release.status !== 3) {
          //     return true;
          //   }
          //   return false;
          // });
          // return !releaseAvailable;
        ),
      end_time: yup
        .number()
        .typeError("Invalid end date")
        .required()
        .test("greater-than-start", "End date should greater than start date", (val: any) => {
          const out = moment(new Date(val)).isAfter(moment(payload.start_time));
          if (out) {
            return true;
          }
          return false;
        }),
      max_supply: yup
        .number()
        .typeError("Supply cannot be empty.")
        .min(1, "Supply should be more than 0.")
        .max(maxSupplyValidationNumber, "Release supply cannot be more than available supply")
        .required("Release supply is required"),
      price: yup.number().typeError("Price must be a number.").min(0).required("Price is required"),
      is_whitelisted: yup.boolean(),
      event: yup
        .object()
        .shape(
          isReleaseEventCreated
            ? {
                name: yup
                  .string()
                  .matches(/^[a-zA-Z0-9 ]*$/, "Enter only alphanumeric characters.")
                  .required("Please provide an event name"),
              }
            : {},
        )
        .required(),
    });
    const result = await schema
      .validate(payload, { abortEarly: false })
      .then(() => true)
      .catch((error: any) => {
        if (error.inner && error.inner.length > 0) {
          const firstError = error.inner[0];
          setErrorState({
            [firstError.path]: firstError.message,
          });
        }
      });

    return result;
  };

  const open = () => {
    setOpenModal(() => true);
  };

  const close = () => {
    setOpenModal(() => false);
    setEditReleaseId(undefined);
    setErrorState({});
    handleResetScheduleData();
  };

  const handleChange = (event: any, property?: string, propValue?: any) => {
    if (!event) {
      setData((prev: any) => ({ ...prev, [property as any]: propValue }));
    } else {
      const { name, value } = event.target;
      setData((prev: any) => ({ ...prev, [name]: value }));
    }

    if (Object.keys(errorState).length) {
      setErrorState({});
    }
  };

  const onReleaseCreateSuccess = (releaseData: any) => {
    toast.success("Created a release", TOAST_CONFIG);
    close();
    reloadMagnetDetails();
    handleResetScheduleData();
    setGlobalLoader(false);
    return releaseData;
  };

  const onReleaseCreateFailed = (err: any, { response }: any) => {
    setGlobalLoader(false);
    const { data: errorData } = response;
    const errors = {} as any;
    if (typeof errorData?.error === "object") {
      Object.keys(errorData.error).forEach((key) => {
        errors[key] = errorData.error[key].join(", ");
      });
      setErrorState(errors);
    }
    toast.error("Create release failed", TOAST_CONFIG);
  };

  const handleCreateDraftRelease = async () => {
    const { startTime, supplyForRelease } = data;

    // console.log(supplyForRelease,data);

    if (supplyForRelease < 1) {
      setErrorState((e: Object) => ({ ...e, max_supply: "* Ensure this value is greater than or equal to 1." }));
      return null;
    }

    if (!startTime) {
      setErrorState((e: Object) => ({ ...e, start_time: "* Start Time is Required" }));
      // toast.error("Invalid end time", TOAST_CONFIG);
      return null;
    }

    const endTime = noEndReleaseTime ? startTime.clone().add(10, "years") : data.endTime;

    if (!endTime) {
      setErrorState((e: Object) => ({ ...e, end_time: "* End time is Required" }));
      // toast.error("Invalid end time", TOAST_CONFIG);
      return null;
    }

    const releaseDataWithEvent = {
      start_time: startTime.valueOf(),
      end_time: endTime.valueOf(),
      max_supply: data.supplyForRelease,
      price: data.salePrice,
      is_whitelisted: isWhitelistedRelease,
      event: isReleaseEventCreated ? data.event : {},
    } as CreateUpdateRelease;

    const isValid = await runValidation(releaseDataWithEvent);
    if (!isValid) {
      toast.error("Invalid release info.", TOAST_CONFIG);
      return null;
    }

    const { event, ...releaseDataWithoutEvent } = releaseDataWithEvent;

    setGlobalLoader(true);
    return post(
      getCreateReleaseEndpoint(magnetIdFromUrl),
      isReleaseEventCreated ? releaseDataWithEvent : releaseDataWithoutEvent,
      {
        processData: onReleaseCreateSuccess,
        processError: onReleaseCreateFailed,
      },
    );
  };

  const onReleaseEditButtonClick = (releaseId: number) => {
    setEditReleaseId(releaseId);
    // Set release data.
    const releaseToBeEdited = releases.find((rel: any) => rel.id === releaseId);
    if (!releaseToBeEdited) {
      toast.error("Release data not found.", TOAST_CONFIG);
      return;
    }
    open();
    // Taking data from backend and updating the release modal fields
    // here start and end time in utc, we converting it into local time
    setIsWhitelistedRelease(releaseToBeEdited?.is_whitelisted);
    setIsReleaseEventCreated(!isObjectEmpty(releaseToBeEdited.event));
    setData({
      // 2022-10-20 Format understood by field.
      startTime: moment(releaseToBeEdited.start_time),
      endTime: moment(releaseToBeEdited.end_time),
      supplyForRelease: releaseToBeEdited.max_supply,
      salePrice: releaseToBeEdited.price,
      isWhitelisted: releaseToBeEdited.is_whitelisted,
      event: releaseToBeEdited.event,
    });
  };

  const handleReleaseUpdateError = (err: any, { response }: any) => {
    setGlobalLoader(false);
    const { data: errorData } = response;
    const errors = {} as any;
    if (typeof errorData?.error === "object") {
      Object.keys(errorData.error).forEach((key) => {
        errors[key] = errorData.error[key].join(", ");
      });
      setErrorState(errors);
    }
    toast.error("Failed to update release.", TOAST_CONFIG);
  };

  const handleReleaseUpdateSuccess = () => {
    setGlobalLoader(false);
    toast.success("Release Updated Successfully.", TOAST_CONFIG);
    setIsManageMagnetReleaseModalOpen(false);
    close();
    reloadMagnetDetails();
    handleResetScheduleData();
    setEditReleaseId(undefined);
  };

  const handleUpdateRelease = async (releaseToEdit: number, skipValidation: boolean = false) => {
    if (!releaseToEdit) {
      toast.error("No Edit Release Id ");
      return;
    }
    const { startTime } = data;

    if (!startTime) {
      setErrorState((e: Object) => ({ ...e, start_time: "* Start Time is Required" }));
      // toast.error("Invalid end time", TOAST_CONFIG);
      return;
    }

    const endTime = noEndReleaseTime ? startTime.clone().add(10, "years") : data.endTime;

    if (!endTime) {
      setErrorState((e: Object) => ({ ...e, end_time: "* End time is Required" }));
      return;
    }

    const updatedReleaseData = {
      // start_time: convertLocalDateTimeToUTC(startTime),
      start_time: startTime.valueOf(),
      end_time: endTime.valueOf(),
      max_supply: data.supplyForRelease,
      price: data.salePrice,
      is_whitelisted: isWhitelistedRelease,
      event: isReleaseEventCreated ? data.event : {},
    } as CreateUpdateRelease;

    const isValid = await runValidation(updatedReleaseData, true);
    if (!isValid && !skipValidation) {
      toast.error("Failed validation.", TOAST_CONFIG);
      return;
    }

    setGlobalLoader(true);
    patch(getReleaseUpdateEndpoint(magnetIdFromUrl, releaseToEdit), updatedReleaseData, {
      processData: handleReleaseUpdateSuccess,
      processError: handleReleaseUpdateError,
    });
  };

  const handlePrivateReleaseChange = async (releaseToEdit: number, isPrivateRelease: boolean) => {
    if (!releaseToEdit) {
      toast.error("Unexpected Error, Please try again.", TOAST_CONFIG);
      return;
    }

    const updatedReleaseData = {
      event: isPrivateRelease ? { name: "" } : {},
      price: 0,
    };

    setGlobalLoader(true);
    patch(getReleaseUpdateEndpoint(magnetIdFromUrl, releaseToEdit), updatedReleaseData, {
      processData: handleReleaseUpdateSuccess,
      processError: handleReleaseUpdateError,
    });
  };

  const handleWhitelistingChange = async (releaseToEdit: number, file?: File) => {
    if (!releaseToEdit) {
      throw new Error("Unexpected Error, Please try again.");
    }

    const whitelistingData = {
      is_whitelisted: !!file,
      whitelisted_file: file,
    };

    setGlobalLoader(true);
    return patch(
      getReleaseUpdateEndpoint(magnetIdFromUrl, releaseToEdit),
      file ? createFormDataFromJSON(whitelistingData) : whitelistingData,
      {
        headers: file
          ? {
              "Content-Type": "multipart/form-data",
            }
          : {},
        processData: handleReleaseUpdateSuccess,
        processError: handleReleaseUpdateError,
      },
    );
  };

  const handleEndRelease = async (releaseId: number) => {
    setEditReleaseId(releaseId);
    // Set release data.
    const releaseToBeEdited = releases.find((rel) => rel.id === releaseId);
    if (!releaseToBeEdited) {
      toast.error("Release data not found.", TOAST_CONFIG);
      return;
    }
    setGlobalLoader(true);
    await post(
      getReleaseEndEndpoint(magnetIdFromUrl, releaseId),
      {},
      {
        processData: handleReleaseUpdateSuccess,
        processError: (errorMessage) => {
          setGlobalLoader(false);
          toast.error(errorMessage, TOAST_CONFIG);
        },
      },
    );
  };

  const handleDeleteRelease = async (releaseId: string) => {
    setGlobalLoader(true);
    await remove(`${BRAND_MAGNET + magnetIdFromUrl + RELEASE}/${releaseId}`, {
      processData: () => {
        toast.success("Deleted Release", TOAST_CONFIG);
        reloadMagnetDetails();
        handleResetScheduleData();
        setGlobalLoader(false);
      },
      // TODO Fix this up
      processError: () => {
        toast.error("Failed to Delete Release", TOAST_CONFIG);
        setGlobalLoader(false);
      },
    });
    close();
  };

  const handlePublishRelease = async (releaseId: string) => {
    setGlobalLoader(true);
    post(
      getPublishReleaseEndpoint(magnetIdFromUrl, releaseId),
      {},
      {
        processData: () => {
          toast.success("Published Release for the Magnet.", TOAST_CONFIG);
          reloadMagnetDetails();
          setGlobalLoader(false);
        },
        processError: () => {
          toast.error("Failed to Publish Release for Magnet.", TOAST_CONFIG);
          setGlobalLoader(false);
        },
      },
    );
    close();
  };

  return (
    <>
      <div className="flex w-auto">
        <div className="my-4 flex w-full justify-between">
          <ButtonWithIcon
            icon={<RefreshIcon className={clsx("h-6 w-5", isRefetching && "animate-spin")} />}
            label="Refresh"
            labelClasses="!font-semibold text-xs 2xl:text-sm"
            onClick={reloadMagnetDetails}
          />
          <Button
            disabled={!isMagnetPublished}
            additionalClasses="xl:text-xs 2xl:text-sm xl:px-5 xl:py-2 2xl:px-7 2xl:py-3"
            variant="magnetiqSolid"
            text="SCHEDULE RELEASE"
            onClick={open}
          />
        </div>
        {/* Model for Creating/Editing Release */}
        <ReleaseModal
          data={data}
          maxEndTimeForRelease={maxEndTimeForRelease}
          open={openModal}
          onClose={close}
          handleFieldChange={handleChange}
          editReleaseId={editReleaseId}
          errorState={errorState}
          handleCreateDraftRelease={handleCreateDraftRelease}
          handleUpdateRelease={handleUpdateRelease}
          toggleIsEndDateProvided={setNoReleaseTime}
          currentlyEditingRelease={currentlyEditingRelease}
          isEndDateProvided={noEndReleaseTime}
          magnetAvailableSupply={magnetAvailableSupply}
        />
      </div>
      <ReleaseTable
        handleEndRelease={handleEndRelease}
        handleDeleteRelease={handleDeleteRelease}
        onReleaseEditClick={onReleaseEditButtonClick}
        isManageMagnetReleaseModalOpen={isManageMagnetReleaseModalOpen}
        setIsManageMagnetReleaseModalOpen={setIsManageMagnetReleaseModalOpen}
        data={releases}
        magnetMaxSupply={magnetMaxSupply}
        handlePublishRelease={handlePublishRelease}
        handlePrivateReleaseChange={handlePrivateReleaseChange}
        handleWhitelistingChange={handleWhitelistingChange}
        handleCreateNewRelease={open}
        isMagnetPublished={isMagnetPublished}
      />
    </>
  );
}
